import { api } from '@/lib/api/client';
import type { AuthUser } from '@shared';
import type { Gender } from '@shared/enums/domain.enum';
import type { PrivacyCookieRule } from '@shared/enums/user.enum';
import type {
  AdminStatsResponse,
  PlayerHistoryResponse,
  PlayerStatsResponse,
  UserPublicProfile,
} from '@shared/types/entities';

export type {
  AdminStatsResponse,
  PlayerHistoryEntry,
  PlayerHistoryResponse,
  PlayerStatsResponse,
  UserPublicProfile,
} from '@shared/types/entities';

export interface UpdateMePayload {
  firstName?: string;
  lastName?: string;
  username?: string;
  mobile?: string;
  licenseNumber?: string;
  birthDate?: string;
  gender?: Gender;
  privacyCookies?: PrivacyCookieRule;
}

// ─── API functions ───────────────────────────────────────────────

export async function getMeProfileApi(): Promise<AuthUser> {
  return api.get('/users/me');
}

export async function getMyStatsApi(): Promise<PlayerStatsResponse> {
  return api.get('/users/me/stats');
}

export async function getMyHistoryApi(params?: {
  page?: number;
  limit?: number;
  year?: number;
}): Promise<PlayerHistoryResponse> {
  const qs = new URLSearchParams();
  if (params?.page) qs.set('page', String(params.page));
  if (params?.limit) qs.set('limit', String(params.limit));
  if (params?.year) qs.set('year', String(params.year));
  const query = qs.toString();
  return api.get(`/users/me/history${query ? `?${query}` : ''}`);
}

export async function updateMyProfileApi(data: UpdateMePayload): Promise<AuthUser> {
  return api.patch('/users/me', data);
}

// ─── Public profile endpoints (by user id) ───────────────────────

export async function getUserProfileApi(id: number): Promise<UserPublicProfile> {
  return api.get(`/users/${id}/profile`);
}

export async function getUserStatsApi(id: number): Promise<PlayerStatsResponse> {
  return api.get(`/users/${id}/stats`);
}

export async function getUserHistoryApi(
  id: number,
  params?: { page?: number; limit?: number; year?: number },
): Promise<PlayerHistoryResponse> {
  const qs = new URLSearchParams();
  if (params?.page) qs.set('page', String(params.page));
  if (params?.limit) qs.set('limit', String(params.limit));
  if (params?.year) qs.set('year', String(params.year));
  const query = qs.toString();
  return api.get(`/users/${id}/history${query ? `?${query}` : ''}`);
}

// ─── Admin stats (SUPER_ADMIN only) ─────────────────────────────

export async function getAdminStatsApi(): Promise<AdminStatsResponse> {
  return api.get('/users/admin/stats');
}
